import CustomModal from 'components/CustomModal';
import { useEffect, useState } from 'react';
import { ICalculateModalProps } from 'interfaces/components/calculateModal';
import { ModalFieldWithChildren } from 'interfaces/constants/CalculateModalConfig';
import CustomModalHeader from 'components/CustomModal/header';
import CustomModalButtons from 'components/CustomModal/buttons';
import CalculateModalBody from './body';
import { CalculateFieldNames } from 'utils/constants/fieldNames';

const CalculateModal = ({
  isOpen,
  onClose,
  config,
  updateDependentFields
}: ICalculateModalProps) => {
  const {
    title,
    description,
    fields,
    statesAndHandlers,
    descriptionParts,
    multipleCalculatedFields
  } = config;
  const [localStates, setLocalStates] = useState(statesAndHandlers.states);
  const [calculatedFields, setCalculatedFields] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    const initialCalculatedFields: Record<string, string | null> = {};
    const newLocalStates = { ...statesAndHandlers.states };

    if (multipleCalculatedFields) {
      multipleCalculatedFields.forEach(field => {
        const result = field.calculate(newLocalStates);
        initialCalculatedFields[field.name] = result ?? null;
        newLocalStates[field.name] = result ?? null;
      });
    }

    fields.forEach(field => {
      if (field.children) {
        const result = field.children.calculate(newLocalStates);
        initialCalculatedFields[field.name] = result ?? null;
        newLocalStates[field.name] = result ?? null;
      }
    });

    setLocalStates(newLocalStates);
    setCalculatedFields(initialCalculatedFields);
  }, [statesAndHandlers.states, fields, multipleCalculatedFields]);

  const handleFieldChange = (fieldName: string, value: string) => {
    const updatedStates = {
      ...localStates,
      [fieldName]: value
    };

    const newStates = updateDependentFields(fieldName, updatedStates);

    const finalUpdatedStates = {
      ...updatedStates,
      ...newStates
    };

    setLocalStates(finalUpdatedStates);

    const updatedCalculatedFields = { ...calculatedFields };

    if (multipleCalculatedFields) {
      multipleCalculatedFields.forEach(field => {
        const result = field.calculate(finalUpdatedStates);
        updatedCalculatedFields[field.name] = result ?? null;
      });
    }

    fields.forEach(field => {
      if (field.children) {
        const result = field.children.calculate(finalUpdatedStates);
        updatedCalculatedFields[field.name] = result ?? null;
      }
    });

    setCalculatedFields(updatedCalculatedFields);
  };

  const [nestedFieldsVisibility, setNestedFieldsVisibility] = useState<
    Record<string, boolean>
  >({});

  const handleToggleNestedFields = (field: ModalFieldWithChildren) => {
    setNestedFieldsVisibility(prevState => ({
      ...prevState,
      [field.name]: !prevState[field.name]
    }));
  };

  const handleApply = () => {
    Object.entries(localStates).forEach(([fieldName, value]) => {
      if (value !== null && value !== statesAndHandlers.states[fieldName]) {
        statesAndHandlers.handlers[fieldName]?.(value);
      }
    });

    if (multipleCalculatedFields) {
      multipleCalculatedFields.forEach(field => {
        const result = field.calculate(localStates);
        const value = result ?? null;
        if (value !== null) {
          statesAndHandlers.handlers[field.name](value);
        }
      });
    }

    onClose();
  };

  const handleSubmitDisable = () => {
    const hasEmptyFields =
      multipleCalculatedFields?.some(field => {
        // Special exception for burn_multiple to enable button if fields are entered but calculation returns null
        if (field.name === 'burn_multiple') {
          const fcfValue = localStates[CalculateFieldNames.FCF];
          const netNewArrValue = localStates[CalculateFieldNames.NET_NEW_ARR];

          if (fcfValue && netNewArrValue) return false;
        }

        return !calculatedFields[field.name];
      }) ?? true;

    if (hasEmptyFields) return true;

    return false;
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        className='max-w-[500px] rounded-xl overflow-hidden'
      >
        <div className='p-8 flex flex-col gap-8'>
          <CustomModalHeader
            title={title}
            description={description}
            descriptionParts={descriptionParts}
            onClose={onClose}
          />
          <CalculateModalBody
            fields={fields}
            title={title}
            localStates={localStates}
            calculatedFields={calculatedFields}
            nestedFieldsVisibility={nestedFieldsVisibility}
            handleToggleNestedFields={handleToggleNestedFields}
            handleFieldChange={handleFieldChange}
            multipleCalculatedFields={multipleCalculatedFields}
          />
          <CustomModalButtons
            disabled={handleSubmitDisable()}
            onClose={onClose}
            handleApply={handleApply}
            firstBtnLabel='Apply and Back'
            firstBtnId='apply_calculate_button'
          />
        </div>
      </CustomModal>
    </>
  );
};

export default CalculateModal;
